(ns heraldicon.frontend.user.session
  (:require
   [clojure.string :as str]
   [com.wsscode.async.async-cljs :refer [<? go-catch]]
   [heraldicon.entity.user :as user]
   [heraldicon.frontend.api :as api]
   [heraldicon.frontend.message :as-alias message]
   [heraldicon.frontend.repository.core :as repository]
   [hodgepodge.core :as hp]
   [re-frame.core :as rf]
   [taoensso.timbre :as log]))

(def ^:private db-path
  [:session])

(def ^:private local-storage-session-id-name
  "cl-session-id")

(def ^:private local-storage-user-id-name
  "cl-user-id")

(def ^:private local-storage-username-name
  "cl-username")

(def ^:private local-storage-dark-mode-name
  "cl-dark-mode")

(def ^:private local-storage-height-limit-mode-name
  "cl-height-limit-mode")

(defn- set-storage-item [key value]
  (if value
    (hp/set-item hp/local-storage key value)
    (hp/remove-item hp/local-storage key)))

(defn- get-storage-item [key]
  (hp/get-item hp/local-storage key))

(defn- validate-session [session-data]
  (go-catch
   (try
     (let [result (<? (api/call :validate-session nil session-data))]
       ;; only an answer without a user means the backend doesn't know the session
       (if result
         (rf/dispatch [::refresh result])
         (rf/dispatch [::session-expired])))
     (catch :default e
       ;; e.g. a backend restart during a deploy; the session may well be valid,
       ;; and calls that need a login still end it if it isn't
       (log/warn e "Session validation failed, keeping the session")))))

(defonce ^:private initial-load?
  (atom true))

(defn read-from-storage []
  (let [session-id (get-storage-item local-storage-session-id-name)
        user-id (get-storage-item local-storage-user-id-name)
        username (get-storage-item local-storage-username-name)
        dark-mode? (= (get-storage-item local-storage-dark-mode-name) "true")
        height-limit-mode? (= (get-storage-item local-storage-height-limit-mode-name) "true")
        session-data {:username username
                      :session-id session-id
                      :user-id user-id
                      :dark-mode? dark-mode?
                      :height-limit-mode? height-limit-mode?}
        validate? @initial-load?]
    (reset! initial-load? false)
    (rf/dispatch-sync [::store session-data])
    (when (and session-id validate?)
      (validate-session session-data))))

(rf/reg-fx ::set-cookie
  (fn [[session-id]]
    (if (str/blank? session-id)
      (set! js/document.cookie (str "session-id="
                                    ";path=/"
                                    ";Max-Age=-99999999"))
      (set! js/document.cookie (str "session-id=" session-id
                                    ";path=/")))))

(rf/reg-fx ::write-to-local-storage
  (fn [[{:keys [session-id username user-id dark-mode? height-limit-mode?]}]]
    (set-storage-item local-storage-session-id-name session-id)
    (set-storage-item local-storage-username-name username)
    (set-storage-item local-storage-user-id-name user-id)
    (set-storage-item local-storage-dark-mode-name (when dark-mode?
                                                     "true"))
    (set-storage-item local-storage-height-limit-mode-name (when height-limit-mode?
                                                             "true"))))

(rf/reg-event-fx ::store
  (fn [{:keys [db]} [_ session-data]]
    {:db (assoc-in db db-path session-data)
     :dispatch [::message/clear-all]
     ::set-cookie [(:session-id session-data)]
     ::write-to-local-storage [session-data]}))

;; Refresh the server-authoritative fields (username, user-id) of an existing
;; session without touching the cookie or clearing messages. Lets a server-side
;; rename surface on the next page load instead of sticking until re-login.
(rf/reg-event-fx ::refresh
  (fn [{:keys [db]} [_ {:keys [username user-id]}]]
    (let [session-data (get-in db db-path)
          refreshed (assoc session-data
                           :username username
                           :user-id user-id)]
      (when (not= session-data refreshed)
        {:db (assoc-in db db-path refreshed)
         ::write-to-local-storage [refreshed]}))))

(rf/reg-event-fx ::clear
  (fn [{:keys [db]} _]
    {:db (assoc-in db db-path nil)
     ::set-cookie [nil]
     ::write-to-local-storage [nil]}))

(rf/reg-sub ::logged-in?
  (fn [_ _]
    [(rf/subscribe [:get (conj db-path :session-id)])
     (rf/subscribe [:get (conj db-path :username)])
     (rf/subscribe [:get (conj db-path :user-id)])])

  (fn [[session-id username user-id] _]
    (and session-id username user-id)))

(rf/reg-sub ::dark-mode?
  (fn [_ _]
    (rf/subscribe [:get (conj db-path :dark-mode?)]))

  (fn [dark-mode? _]
    dark-mode?))

(rf/reg-sub ::height-limit-mode?
  (fn [_ _]
    (rf/subscribe [:get (conj db-path :height-limit-mode?)]))

  (fn [height-limit-mode? _]
    height-limit-mode?))

(defn data-from-db [db]
  (get-in db db-path))

(rf/reg-sub ::data
  (fn [_ _]
    (rf/subscribe [:get db-path]))

  (fn [data _]
    data))

(rf/reg-sub ::admin?
  (fn [_ _]
    (rf/subscribe [:get db-path]))

  (fn [data _]
    (user/admin? data)))

(rf/reg-fx ::backend-logout
  (fn [[session-data]]
    (when (:session-id session-data)
      (go-catch
       (try
         (<? (api/call :logout nil session-data))
         (catch :default e
           (log/warn e "Backend logout failed, local session cleared anyway.")))))))

(rf/reg-event-fx ::logout
  (fn [{:keys [db]} _]
    {::backend-logout [(get-in db db-path)]
     :dispatch-n [[::clear]
                  [::repository/session-change]]
     ::set-cookie [nil]
     ::write-to-local-storage [nil]}))

(rf/reg-event-fx ::session-expired
  (fn [_ _]
    {:dispatch [::clear]}))

(rf/reg-event-fx ::toggle-dark-mode
  (fn [{:keys [db]} _]
    (let [new-db (update-in db (conj db-path :dark-mode?) not)
          session-data (get-in new-db db-path)]
      {:db new-db
       ::write-to-local-storage [session-data]})))

(rf/reg-event-fx ::toggle-height-limit-mode
  (fn [{:keys [db]} _]
    (let [new-db (update-in db (conj db-path :height-limit-mode?) not)
          session-data (get-in new-db db-path)]
      {:db new-db
       ::write-to-local-storage [session-data]})))
